import {
  CellValue,
  ChartConfig,
  ColumnDescriptor,
  FieldMetaCtx,
  getFieldMeta,
  QueryResponse,
  QueryResultBlock
} from '@blisspointmedia/bpm-types/dist/dashboarding';
import { Sheet } from '@src/utils/download-utils';

// Sheet names for a comparison chart's two query blocks — the same words the chart itself uses for
// the two periods (see the scatter cloud labels in chart-renderer-utils), so an exported workbook
// reads the way the chart does rather than exposing raw query ids.
const BASE_PERIOD_SHEET = 'This period';
const PRIOR_PERIOD_SHEET = 'Prior period';

// The header for one response column: the backend's own name when it sends one, else the
// metric-column registry's pretty label for the ref — the same source the chart's axis and table
// headers use, so "q0.col:spend" exports as "Spend".
const columnLabel = (descriptor: ColumnDescriptor, ctx?: FieldMetaCtx): string =>
  descriptor.name ?? getFieldMeta(descriptor.id, { role: descriptor.role, ctx }).label;

// Flatten one query block's columnar payload (`{ 'q0.col:spend': [100, 120] }`) into row objects
// keyed by header label.
const blockRows = (block: QueryResultBlock, ctx?: FieldMetaCtx): Record<string, CellValue>[] => {
  // Allowlist the roles the export shows: icon keys (`dimMetaData`) and any future role stay out.
  const columns = block.schema
    .filter(descriptor => descriptor.role === 'dimension' || descriptor.role === 'metric')
    .map(descriptor => ({
      label: columnLabel(descriptor, ctx),
      values: block.data[descriptor.id] ?? []
    }));

  // Length comes from the longest column rather than `block.rowCount` so a row is never dropped if
  // the two disagree; a column that runs short pads with null. Matches prepareTable's approach.
  const rowCount = Math.max(0, ...columns.map(column => column.values.length));
  return Array.from({ length: rowCount }, (_, i) =>
    Object.fromEntries(columns.map(column => [column.label, column.values[i] ?? null]))
  );
};

const sheetName = (queryId: string, config: ChartConfig, blockCount: number): string => {
  // A one-query chart (the common case) has nothing to disambiguate, so the chart's own title names
  // its single sheet.
  if (blockCount === 1) return config.title;
  if (queryId === config.comparison?.base) return BASE_PERIOD_SHEET;
  if (queryId === config.comparison?.prior) return PRIOR_PERIOD_SHEET;
  return queryId;
};

// The chart's underlying data as Excel sheets — one per query block, read straight off the
// QueryResponse rather than off a renderer's prepared shape.
//
// Going through the response (not `prepareGraphicChart` / `prepareTable` / `prepareStat`) is what
// keeps this to ONE code path for every chart type: line, area, bar, scatter, table and stat all
// return the same columnar payload. It also exports the COMPLETE result — the prepared shapes cap
// rows (`rowLimit`) and series (the "+N more" notice) for legibility on screen, and an export that
// silently dropped those rows would be worse than useless. Values stay raw (numbers as numbers,
// dates as their ISO strings) so Excel can sum and pivot them; only the headers are humanized.
export const chartExportSheets = (
  config: ChartConfig,
  data: QueryResponse,
  ctx?: FieldMetaCtx
): Sheet[] => {
  const blocks = Object.entries(data.queries);
  return blocks.map(([queryId, block]) => ({
    name: sheetName(queryId, config, blocks.length),
    data: blockRows(block, ctx)
  }));
};

// Does the response carry any rows at all? Reads only each block's declared `rowCount`, so it is
// cheap enough to call on every render — unlike building the sheets above, which is deferred until
// the user actually picks the download.
export const chartHasData = (data: QueryResponse): boolean =>
  Object.values(data.queries).some(block => (block?.rowCount ?? 0) > 0);

// `<company>_<chart title>`, with format-hostile characters dropped and whitespace collapsed to
// underscores — matching how the app's other exports name their files (e.g. `acme_saturation`).
export const chartExportFileName = (company: string, title: string): string =>
  [
    company,
    title
      .replace(/[^\w\s-]/g, '')
      .trim()
      .replace(/\s+/g, '_')
  ]
    .filter(Boolean)
    .join('_');
